/* eslint-disable react/no-array-index-key */
import { FC, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { Tooltip } from 'react-tooltip';

import { State } from 'redux/initialState';
import { dateFormat } from 'constants/formats';
import { useTranslate } from 'hooks/useTranslate';

import createPartnersArray from '../utils/createPartnersArray';
import { getFilterValue } from '../utils/getFilterValue';
import {
  columns,
  titleColumn,
  transactionNames,
} from '../constants/partnerBookConstants';
import DownloadButton from './DownloadButton';

const ROW_HEIGHT = 30;
const HEADER_HEIGHT = 49;
const SUBHEADER_HEIGHT = 30;
const NAME_COLUMN_WIDTH = 200;
const DATA_COLUMN_WIDTH = 80;
const VIEWPORT_HEIGHT = 600;

type PartnerBookVirtualTableProps = {
  bookDates: Record<string, string>;
  bookColumns: Record<string, string>;
  bookHeaders: Record<string, string>;
  bookComments: Record<string, string>;
  bookPartners: unknown;
  bookOperations: Record<string, string>;
  bookTotals: Record<string, string | number>;
  bookTotalPartners?: number;
  bookOrder: Record<string, number>;
  bookDocuments: Record<string, string | string[]>;
};

const getCellClassName = (index: number, bookOrder: Record<string, number>) => {
  if (bookOrder[index] % 2 === 0) {
    return 'bg-light-vert text-right';
  }
  return 'text-right';
};

const PartnerBookVirtualTable: FC<PartnerBookVirtualTableProps> = ({
  bookDates,
  bookColumns,
  bookHeaders,
  bookComments,
  bookPartners,
  bookOperations,
  bookTotals,
  bookTotalPartners,
  bookOrder,
  bookDocuments,
}) => {
  const { t } = useTranslate();
  const scrollRef = useRef<HTMLDivElement>(null);

  const societyId = useSelector(
    (state: State) => state.society?.actualSociety?.['_id']
  );
  const isAdmin = useSelector((state: State) => state.society?.role?.isAdmin);

  const partnersArray = useMemo(
    () => createPartnersArray(bookPartners as Record<string, unknown>),
    [bookPartners]
  );

  const bookPartnersMatrix = useMemo(
    () =>
      partnersArray.map(
        (partner: { data: (string | number)[] }) => partner.data
      ),
    [partnersArray]
  );

  const columnIndices = useMemo(
    () =>
      Object.keys(bookColumns || {})
        .map(Number)
        .sort((a, b) => a - b),
    [bookColumns]
  );

  const dataColumnIndices = useMemo(
    () => columnIndices.filter((index) => index > 0),
    [columnIndices]
  );

  const operationHeaderGroups = useMemo(() => {
    const groups: Array<{
      index: number;
      header: string;
      colSpan: number;
      width: number;
    }> = [];

    Object.entries(bookHeaders || {}).forEach(([, header], index) => {
      if (header === '') return;

      const operationType = bookOperations[index];
      const colSpan =
        operationType && operationType !== ''
          ? columns[operationType as keyof typeof columns]?.colSpan || 1
          : 1;

      groups.push({
        index,
        header,
        colSpan,
        width: colSpan * DATA_COLUMN_WIDTH,
      });
    });

    return groups;
  }, [bookHeaders, bookOperations]);

  const totalWidth =
    NAME_COLUMN_WIDTH + dataColumnIndices.length * DATA_COLUMN_WIDTH;

  const rowVirtualizer = useVirtualizer({
    count: partnersArray.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: dataColumnIndices.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => DATA_COLUMN_WIDTH,
    overscan: 8,
  });

  const formatFooterValue = (index: number, total: string | number) => {
    if (total === '' || total === null || total === undefined) return null;

    const isPercentColumn = bookColumns[index] === titleColumn.PERCENT;
    const numericValue = Number(total);

    if (Number.isNaN(numericValue)) return null;

    if (isPercentColumn) {
      return `${numericValue.toFixed(2)}%`;
    }

    return numericValue.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const virtualColumns = columnVirtualizer.getVirtualItems();
  const virtualRows = rowVirtualizer.getVirtualItems();

  return (
    <div className="partnerBookTableWrapper card card-bordered partnerBookVirtualWrapper">
      <div
        ref={scrollRef}
        className="partnerBookVirtualScroll"
        style={{ height: `${VIEWPORT_HEIGHT}px` }}
      >
        <div
          className="partnerBookVirtualInner"
          style={{ width: `${totalWidth}px`, minWidth: '100%' }}
        >
          {/* Sticky header */}
          <div
            className="partnerBookVirtualHeader"
            style={{ width: `${totalWidth}px` }}
          >
            {/* Top header row: operation groups */}
            <div
              className="partnerBookVirtualHeaderRow table-header"
              style={{ height: HEADER_HEIGHT }}
            >
              <div
                className="partnerBookVirtualCornerCell partnerBookVirtualStickyCorner"
                style={{
                  width: NAME_COLUMN_WIDTH,
                  minWidth: NAME_COLUMN_WIDTH,
                  height: HEADER_HEIGHT,
                }}
              />
              <div
                className="partnerBookVirtualHeaderGroups"
                style={{
                  position: 'relative',
                  width: `${columnVirtualizer.getTotalSize()}px`,
                  height: HEADER_HEIGHT,
                }}
              >
                {(() => {
                  let offset = 0;
                  return operationHeaderGroups.map((group) => {
                    const left = offset;
                    offset += group.width;

                    return (
                      <div
                        key={`header-group-${group.index}`}
                        className={`partnerBookVirtualHeaderGroup ${
                          bookOrder[group.index] % 2 === 0
                            ? 'bg-light-vert'
                            : 'bg-white'
                        }`}
                        style={{
                          position: 'absolute',
                          left: `${left}px`,
                          width: `${group.width}px`,
                          height: HEADER_HEIGHT,
                        }}
                      >
                        <div
                          style={{ zIndex: 1 }}
                          data-tooltip-id={`${group.index}-${societyId}`}
                        >
                          {t(
                            transactionNames[
                              group.header as keyof typeof transactionNames
                            ]
                          )}
                          <br />
                          {bookDates[group.index] !== '' &&
                            format(
                              new Date(bookDates[group.index]),
                              dateFormat
                            )}
                        </div>

                        {isAdmin && (
                          <Tooltip
                            id={`${group.index}-${societyId}`}
                            place="bottom"
                          >
                            {bookComments[group.index]}
                          </Tooltip>
                        )}

                        <DownloadButton
                          documents={bookDocuments?.[group.index]}
                        />
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Subheader row: column labels */}
            <div
              className="partnerBookVirtualHeaderRow table-subheader"
              style={{ height: SUBHEADER_HEIGHT }}
            >
              <div
                className="partnerBookVirtualCornerCell partnerBookVirtualStickyCorner partnerBookVirtualNameHeader"
                style={{
                  width: NAME_COLUMN_WIDTH,
                  minWidth: NAME_COLUMN_WIDTH,
                  height: SUBHEADER_HEIGHT,
                }}
              >
                {t('Partners')}
              </div>
              <div
                className="partnerBookVirtualSubheaderCols"
                style={{
                  position: 'relative',
                  width: `${columnVirtualizer.getTotalSize()}px`,
                  height: SUBHEADER_HEIGHT,
                }}
              >
                {virtualColumns.map((virtualCol) => {
                  const columnIndex = dataColumnIndices[virtualCol.index];
                  const column = bookColumns[columnIndex];

                  return (
                    <div
                      key={`subheader-${columnIndex}`}
                      className={`partnerBookVirtualSubheaderCell no-break ${
                        bookOrder[columnIndex] % 2 === 0
                          ? 'bg-light-vert'
                          : 'bg-white'
                      }`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: `${virtualCol.size}px`,
                        height: SUBHEADER_HEIGHT,
                        transform: `translateX(${virtualCol.start}px)`,
                      }}
                    >
                      {column !== '' && t(column as string)}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Virtual body */}
          <div
            className="partnerBookVirtualBody"
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              position: 'relative',
              width: `${totalWidth}px`,
            }}
          >
            {virtualRows.map((virtualRow) => {
              const partner = partnersArray[virtualRow.index];
              const partnerIndex = virtualRow.index;
              const isEvenRow = partnerIndex % 2 === 0;

              return (
                <div
                  key={String(partner?.id || virtualRow.key)}
                  className={`partnerBookVirtualRow ${
                    isEvenRow ? 'bg-white' : 'bg-light'
                  }`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: `${totalWidth}px`,
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                    display: 'flex',
                  }}
                >
                  <div
                    className={`partnerBookVirtualNameCell ${
                      isEvenRow ? 'bg-white' : 'bg-light'
                    }`}
                    data-tooltip-id={partner?.id}
                    style={{
                      width: NAME_COLUMN_WIDTH,
                      minWidth: NAME_COLUMN_WIDTH,
                      height: ROW_HEIGHT,
                      lineHeight: `${ROW_HEIGHT}px`,
                    }}
                  >
                    <span className="text-left w-200px d-block text-ellipsis pl-1 pr-4">
                      {partner?.data?.[0]}
                    </span>
                    <Tooltip id={partner?.id} place="bottom">
                      {partner?.data?.[0]}
                    </Tooltip>
                  </div>

                  <div
                    className="partnerBookVirtualDataRow"
                    style={{
                      position: 'relative',
                      width: `${columnVirtualizer.getTotalSize()}px`,
                      height: ROW_HEIGHT,
                    }}
                  >
                    {virtualColumns.map((virtualCol) => {
                      const columnIndex = dataColumnIndices[virtualCol.index];

                      return (
                        <div
                          key={`${partner?.id}-${columnIndex}`}
                          className={`partnerBookVirtualDataCell ${getCellClassName(
                            columnIndex,
                            bookOrder
                          )}`}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: `${virtualCol.size}px`,
                            height: ROW_HEIGHT,
                            lineHeight: `${ROW_HEIGHT}px`,
                            transform: `translateX(${virtualCol.start}px)`,
                          }}
                        >
                          {getFilterValue({
                            partnerIndex,
                            index: columnIndex,
                            data: partner?.data?.[columnIndex],
                            column: bookColumns[columnIndex],
                            isForScreen: true,
                            bookPartnersArray: bookPartnersMatrix,
                            bookOperations,
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sticky footer */}
          <div
            className={`partnerBookVirtualFooter ${
              !(bookTotalPartners && bookTotalPartners % 2 === 0)
                ? 'bg-light font-weight-bold'
                : 'font-weight-bold'
            }`}
            style={{ width: `${totalWidth}px`, height: ROW_HEIGHT }}
          >
            <div
              className="partnerBookVirtualCornerCell partnerBookVirtualStickyFooterCorner"
              style={{
                width: NAME_COLUMN_WIDTH,
                minWidth: NAME_COLUMN_WIDTH,
                height: ROW_HEIGHT,
                lineHeight: `${ROW_HEIGHT}px`,
              }}
            >
              {t('TOTAL')}
            </div>
            <div
              className="partnerBookVirtualFooterCols"
              style={{
                position: 'relative',
                width: `${columnVirtualizer.getTotalSize()}px`,
                height: ROW_HEIGHT,
              }}
            >
              {virtualColumns.map((virtualCol) => {
                const columnIndex = dataColumnIndices[virtualCol.index];
                const total = bookTotals[columnIndex];
                const isEvenColumn = bookOrder[columnIndex] % 2 === 0;

                return (
                  <div
                    key={`footer-${columnIndex}`}
                    className={`partnerBookVirtualDataCell ${
                      isEvenColumn ? 'bg-light-vert' : ''
                    }`}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: `${virtualCol.size}px`,
                      height: ROW_HEIGHT,
                      lineHeight: `${ROW_HEIGHT}px`,
                      transform: `translateX(${virtualCol.start}px)`,
                    }}
                  >
                    {formatFooterValue(columnIndex, total)}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerBookVirtualTable;
